import type { Hero, HeroOverride, HeroSkillOverride } from "@/types/heroes";
import type {
  CardCatalogEntry,
  CardDatabase,
  DeckCard,
  BalanceOverrides,
  CardOverride,
} from "@/types/cards";
import { isPhase1Hero } from "./phase1";

// public/data 在 dev 与 build 阶段都会被 syncStaticAssets 插件填充
const BASE_DIR = "/data";

async function fetchJSON<T>(path: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(path, { cache: "no-store" });
    if (!res.ok) return fallback;
    return (await res.json()) as T;
  } catch {
    return fallback;
  }
}

// 合并单技能：override 字段为空时保留基础值
function mergeSkill(
  base: Hero["skills"][number],
  ov: HeroSkillOverride | undefined
): Hero["skills"][number] {
  if (!ov) return base;
  return {
    ...base,
    ...(ov.type !== undefined ? { type: ov.type } : {}),
    ...(ov.manaCost !== undefined ? { manaCost: ov.manaCost } : {}),
    ...(ov.description !== undefined ? { description: ov.description } : {}),
    flags: { ...base.flags, ...(ov.flags || {}) },
  };
}

// 合并英雄：override 顶层字段 + skills 子表
export function mergeHero(base: Hero, ov: HeroOverride | undefined): Hero {
  if (!ov) return base;
  const merged: Hero = {
    ...base,
    ...(ov.hp !== undefined ? { hp: ov.hp } : {}),
    ...(ov.mp !== undefined ? { mp: ov.mp } : {}),
    ...(ov.defense !== undefined ? { defense: ov.defense } : {}),
    ...(ov.magicDefense !== undefined ? { magicDefense: ov.magicDefense } : {}),
    ...(ov.attack !== undefined ? { attack: ov.attack } : {}),
    ...(ov.note !== undefined ? { note: ov.note } : {}),
    ...(ov.faction !== undefined ? { faction: ov.faction } : {}),
    ...(ov.portrait !== undefined ? { portrait: ov.portrait } : {}),
  };
  if (ov.skills) {
    merged.skills = base.skills.map((s) => mergeSkill(s, ov.skills?.[s.name]));
  }
  return merged;
}

// 合并卡牌目录项
export function mergeCard(
  base: CardCatalogEntry,
  ov: CardOverride | undefined
): CardCatalogEntry {
  if (!ov) return base;
  return {
    ...base,
    ...(ov.effectText !== undefined ? { effectText: ov.effectText } : {}),
    ...(ov.responds !== undefined ? { responds: ov.responds } : {}),
    ...(ov.damageType !== undefined ? { damageType: ov.damageType } : {}),
    ...(ov.nonTarget !== undefined ? { nonTarget: ov.nonTarget } : {}),
    ...(ov.rangeBonus !== undefined ? { rangeBonus: ov.rangeBonus } : {}),
    ...(ov.attackBonus !== undefined ? { attackBonus: ov.attackBonus } : {}),
    ...(ov.defenseBonus !== undefined ? { defenseBonus: ov.defenseBonus } : {}),
    ...(ov.magicDefenseBonus !== undefined
      ? { magicDefenseBonus: ov.magicDefenseBonus }
      : {}),
    ...(ov.hpBonus !== undefined ? { hpBonus: ov.hpBonus } : {}),
    ...(ov.equipHeal !== undefined ? { equipHeal: ov.equipHeal } : {}),
    ...(ov.spellDamageBonus !== undefined ? { spellDamageBonus: ov.spellDamageBonus } : {}),
    ...(ov.handLimitBonus !== undefined ? { handLimitBonus: ov.handLimitBonus } : {}),
    ...(ov.manaCostReduction !== undefined ? { manaCostReduction: ov.manaCostReduction } : {}),
    ...(ov.cards !== undefined ? { cards: ov.cards } : {}),
  };
}

export interface LoadedData {
  heroes: Hero[]; // 已合并 overrides（完整 84 名，按需筛选 phase1）
  catalog: CardCatalogEntry[]; // 合并后的目录
  deck: DeckCard[]; // 合并后的一期 deck（156 张，已过滤 phase1）
  fullDeck: DeckCard[]; // 合并后的全部 deck（242 张）
  meta: CardDatabase["meta"];
}

// 主加载入口：浏览器/Vite 环境
export async function loadGameData(): Promise<LoadedData> {
  const [heroes, cards, overrides] = await Promise.all([
    fetchJSON<Hero[]>(`${BASE_DIR}/heroes.json`, []),
    fetchJSON<CardDatabase>(`${BASE_DIR}/cards.json`, {
      meta: {
        source: "",
        totalCards: 0,
        phase1Cards: 0,
        suits: ["红", "黑", "蓝"],
        rankRange: [1, 10],
        kindSummary: {},
        artifactCount: 0,
        statusCount: 0,
      },
      catalog: [],
      deck: [],
      artifacts: [],
      statusCards: [],
    }),
    fetchJSON<BalanceOverrides>(`${BASE_DIR}/balance_overrides.json`, {
      heroes: {},
      cards: {},
      fate: {},
    }),
  ]);

  const mergedHeroes = heroes.map((h) =>
    mergeHero(h, overrides.heroes[h.name])
  );
  const mergedCatalog = cards.catalog.map((c) =>
    mergeCard(c, overrides.cards[c.name])
  );
  // deck 实例补充 catalog 上的伤害类型/装备加成（按 id 关联）
  const catalogById = new Map(mergedCatalog.map((c) => [c.id, c]));
  const enrich = (d: DeckCard): DeckCard => {
    const c = catalogById.get(d.id);
    if (!c) return d;
    return {
      ...d,
      damageType: c.damageType,
      responds: c.responds,
      rangeBonus: c.rangeBonus,
      attackBonus: c.attackBonus,
      defenseBonus: c.defenseBonus,
      magicDefenseBonus: c.magicDefenseBonus,
      hpBonus: c.hpBonus,
      equipHeal: c.equipHeal,
      spellDamageBonus: c.spellDamageBonus,
      handLimitBonus: c.handLimitBonus,
      manaCostReduction: c.manaCostReduction,
      effectText: c.effectText,
    };
  };
  const fullDeck = cards.deck.map(enrich);
  const deck = fullDeck.filter((d) => d.phase1 === true);

  return {
    heroes: mergedHeroes,
    catalog: mergedCatalog,
    deck,
    fullDeck,
    meta: cards.meta,
  };
}

// 一期英雄筛选
export function filterPhase1Heroes(heroes: Hero[]): Hero[] {
  return heroes.filter((h) => isPhase1Hero(h.name));
}

// 按名字查找英雄
export function findHero(heroes: Hero[], name: string): Hero | undefined {
  return heroes.find((h) => h.name === name);
}
